<template>
  <div>
    <div class="modals">
      <ModalWorkpachages :excludePackageMessage="excludePackageMessage()" />
    </div>
    <div v-if="pageData" class="tech-map-page">
      <div class="container-wrapper">
        <div class="container">
          <BackLink
            :to="backButton.route"
            :text="backButton.text"
          />
        </div>
      </div>

      <div class="container">
        <h1 class="tech-map-page__title">{{ pageData.title }}</h1>
      </div>

      <div class="tech-map-page__sticky-header">
        <div class="container">
          <CarInfo
            :image="{ src: pageData.car.image, alt: pageData.car.year}"
            :year="pageData.car.year"
            :modification="pageData.car.modification"
          />

          <PeriodRange
            v-model="currentFilter.period"
            :title="pageData.range.title"
            :steps="pageData.range.steps"
            class="tech-map-page__range"
          />
        </div>
      </div>

      <div class="container-wrapper">
        <section class="container">
          <div class="tech-map-page__filters">
            <div class="tech-map-page__dropdowns">
              <BaseSelect
                v-model="currentFilter.city"
                :label="pageData.filters.labels.city"
                :items="citiesList"
                :desabled="getAliasParamCity"
                required
                :class="[
                  'tech-map-page__dropdown',
                  {'tech-map-page__dropdown_disable' : getAliasParamCity}
                ]"
              />
              <BaseSelect
                v-if="dealersList"
                :key="JSON.stringify(dealersList)"
                v-model="currentFilter.dealer"
                :label="pageData.filters.labels.dealer"
                :items="dealersList"
                :desabled="getAliasParamDealer"
                required
                :class="[
                  'tech-map-page__dropdown',
                  {'tech-map-page__dropdown_disable' : getAliasParamDealer}
                ]"
              />
            </div>
          </div>
        </section>
      </div>

      <transition name="fade-slow" mode="out-in">
        <section
          v-if="isResultVisible"
          class="tech-map-page__content"
        >
          <TechMapFiltrationResult
            :oilsPayload="oil"
            :componentData="resultsData"
            :currentFilter="currentFilter"
            :arrayOil="arrayOil"
            :oilChange="onOilChange"
            @oilChange="onOilChange"
          />
        </section>
      </transition>
    </div>
  </div>
</template>

<script>
  // Step 4

  import { mapGetters, mapActions } from 'vuex'
  import BackLink from '@/components/BackLink'
  import CarInfo from '@/components/CarInfo'
  import PeriodRange from '@/components/PeriodRange'
  import TechMapFiltrationResult from '@/components/TechMapFiltrationResult'
  import { debounce, get } from 'lodash'
  import StickyEvents from 'sticky-events'
  import ModalWorkpachages from '@/components/modal/ModalWorkpachages'
  import { DEBOUNCE_REQUESTS, DEFAULT_LANG } from '@/helpers/constant'

  export default {
    name: 'TechMapPage',

    components: {
      BackLink,
      CarInfo,
      PeriodRange,
      TechMapFiltrationResult,
      ModalWorkpachages
    },

    data () {
      return {
        showResults: false,
        pageData: null,
        resultsData: null,
        currentFilter: {
          period: '',
          city: '',
          dealer: ''
        },
        oil: '',
        oilChangeFirstTime: true,
        arrayOil: []
      }
    },

    computed: {
      ...mapGetters([
        'getModel',
        'getYear',
        'getModification',
        'getPeriod',
        'getCity',
        'getAliasParamCity',
        'getDealer',
        'getAliasParamDealer',
        'getWorkpackages'
      ]),
      citiesList () {
        return this.pageData.filters.list
          .map(item => {
            return { text: item.text, value: item.value }
          })
      },
      dealersList () {
        const currentCity = this.pageData.filters.list
          .filter(city => city.value === this.currentFilter.city)

        return get(currentCity, [0, 'dealers']) ||
          [{ text: this.citiesList[0].text, value: null }] // make sure that this.citiesList[0] is 'not-selected' (null) value
      },
      backButton () {
        const text = this.pageData.backButtonText
        let route
        const lang = this.$store.state.currentLang
        const queryLang = lang !== DEFAULT_LANG ? { lang } : {}
        // if was search by vin code - back to model page
        if (this.$store.state.vinSearchData.modelOpenCampaignsMessage) {
          route = { name: 'model', query: { ...queryLang } }
        } else {
          route = {
            name: 'modification',
            params: {
              model: this.getModel,
              year: this.getYear
            },
            query: {
              ...queryLang
            }
          }
        }

        return { text, route }
      },
      isResultVisible () {
        return this.showResults &&
          this.currentFilter.period &&
          this.currentFilter.city &&
          this.currentFilter.dealer &&
          this.resultsData
      }
    },

    watch: {
      'getWorkpackages': {
        handler: function (search) {
          if (!this.oilChangeFirstTime) {
            this.fetchResultsPackages()
          }
        },
        deep: true,
        immediate: true
      },
      'currentFilter.period': {
        handler: function (val) {
          this.arrayOil = []
          // this.$router.replace({ path: this.$route.fullPath, query: { oil: null } })
        },
        deep: true
      },
      currentFilter: {
        deep: true,
        handler: debounce(function (newValue) {
          this.oilChangeFirstTime = true

          const { period, city, dealer } = newValue

          const lang = this.$store.state.currentLang
          const queryLang = lang !== DEFAULT_LANG ? { lang } : {}

          const newQuery = {
            period,
            city,
            dealer,
            workpackages: this.$route.query.workpackages || null,
            oil: this.$route.query.oil,
            ...queryLang
          }

          if (JSON.stringify(this.$route.query) !== JSON.stringify(newQuery)) {
            this.$router.push({ query: newQuery })
          }
          if (period && city && dealer) {
            this.fetchResults()
          }

          if (!city) {
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }
        }, DEBOUNCE_REQUESTS)
      },
      async pageData (v) {
        if (v) {
          await this.$nextTick()

          const stickyEvents = new StickyEvents({
            enabled: true,
            stickySelector: '.tech-map-page__sticky-header'
          })

          stickyEvents.enableEvents()

          const { stickyElements } = stickyEvents

          stickyElements.forEach(sticky => {
            sticky.addEventListener(StickyEvents.CHANGE, (event) => {
              sticky.classList.toggle('tech-map-page__sticky-header_stuck', event.detail.isSticky)
            })
          })

          this.modelText = get(this, '$store.state.model.modelText', '') || get(v, 'car.toyotaModelName', '')
          this.modelId = get(this, '$store.state.model.modelId', '') || get(v, 'car.toyotaModelId', '')

          this.pushDataToDataLayer({
            page: {
              category: 'tools',
              subCategory: 'maintenance',
              sectionlv1: 'tool',
              sectionlv2: 'maintenance-calculator',
              language: this.$store.state.currentLang
            },
            event: {
              name: 'workflowevent',
              componentName: 'maintenance-calculator',
              componentGroup: 'tools',
              action: 'workflow-step',
              label: 'configure-product'
            },
            workflow: {
              name: 'maintenance-calculator',
              step: 'configure-product',
              conversion: '0'
            },
            product: [{
              category: 'cars',
              subCategory: 'new-cars',
              productInfo: {
                productId: this.modelId,
                productName: this.modelText,
                tmeModelId: this.modelId,
                modelName: this.modelText
              }
            }]
          }, true)
        }
      },
      'currentFilter.dealer' (v) {
        if (v) {
          this.dealerId = this.checkNull(get(get(this, 'dealersList', []).find(item => item.value === v), 'dealerId', ''))
          this.zipcode = this.checkNull(get(get(this, 'dealersList', []).find(item => item.value === v), 'zipcode', ''))
          this.dealerCity = get(this, 'currentFilter.city', '') ? this.checkNull(get(get(this, 'citiesList', []).find(item => item.value === get(this, 'currentFilter.city', '')), 'text', '')) : ''
          this.dealerName = this.checkNull(get(get(this, 'dealersList', []).find(item => item.dealerId === this.dealerId), 'text', ''))

          this.$store.commit('SET_DEALER', {
            dealerName: this.dealerName,
            dealerId: this.dealerId,
            dealerCity: this.dealerCity,
            zipcode: this.zipcode
          })

          this.pushDataToDataLayer({
            page: {
              category: 'tools',
              subCategory: 'maintenance',
              sectionlv1: 'tool',
              sectionlv2: 'maintenance-calculator',
              language: this.$store.state.currentLang
            },
            event: {
              name: 'workflowevent',
              componentName: 'maintenance-calculator',
              componentGroup: 'tools',
              action: 'workflow-step',
              label: 'quotation-completed'
            },
            workflow: {
              name: 'maintenance-calculator',
              step: 'quotation-completed',
              conversion: '1'
            },
            product: [{
              category: 'cars',
              subCategory: 'new-cars',
              productInfo: {
                productId: this.modelId,
                productName: this.modelText,
                tmeModelId: this.modelId,
                modelName: this.modelText
              }
            }],
            dealer: {
              name: this.dealerName,
              dealerId: this.dealerId,
              city: this.dealerCity,
              zipcode: this.zipcode
            }
          }, true)
        }
      },
      $route (to, from) {
        if (from.name === 'modification' && to.name === 'tech-map') {
          this.modelTextState()
          this.modelIdState()

          this.pushDataToDataLayer({
            page: {
              category: 'tools',
              subCategory: 'maintenance',
              sectionlv1: 'tool',
              sectionlv2: 'maintenance-calculator',
              language: this.$store.state.currentLang
            },
            event: {
              name: 'workflowevent',
              componentName: 'maintenance-calculator',
              componentGroup: 'tools',
              action: 'workflow-step',
              label: 'configure-product'
            },
            workflow: {
              name: 'maintenance-calculator',
              step: 'configure-product',
              conversion: '0'
            },
            product: [{
              category: 'cars',
              subCategory: 'new-cars',
              productInfo: {
                productId: this.modelId,
                productName: this.modelText,
                tmeModelId: this.modelId,
                modelName: this.modelText
              }
            }]
          }, true)
        }
        if (from.name === 'service-request' && to.name === 'tech-map') {
          this.modelTextState()
          this.modelIdState()

          this.pushDataToDataLayer({
            page: {
              category: 'tools',
              subCategory: 'maintenance',
              sectionlv1: 'tool',
              sectionlv2: 'maintenance-calculator',
              language: this.$store.state.currentLang
            },
            event: {
              name: 'workflowevent',
              componentName: 'maintenance-calculator',
              componentGroup: 'tools',
              action: 'workflow-step',
              label: 'configure-product'
            },
            workflow: {
              name: 'maintenance-calculator',
              step: 'configure-product',
              conversion: '0'
            },
            product: [{
              category: 'cars',
              subCategory: 'new-cars',
              productInfo: {
                productId: this.modelId,
                productName: this.modelText,
                tmeModelId: this.modelId,
                modelName: this.modelText
              }
            }]
          }, true)
        }
      }
    },

    async created () {
      const data = await this.fetchTechMapPageData({
        modification: this.getModification
      })

      this.pageData = { ...data }

      this.currentFilter.period = this.getPeriod || ''
      this.currentFilter.city = this.getCity || this.getAliasParamCity || ''
      this.currentFilter.dealer = this.getDealer || this.getAliasParamDealer || ''
    },

    deactivated () {
      this.oilChangeFirstTime = false
    },

    activated () {
    },

    methods: {
      ...mapActions(['fetchTechMapPageData', 'fetchTechMapResultsData']),
      excludePackageMessage () {
        return get(this.resultsData, 'tables.required.excludePackageMessage', '')
      },
      async fetchResultsPackages () {
        // this.showResults = false
        const results = await this.fetchTechMapResultsData({
          workpackages: this.$route.query.workpackages || null,
          period: this.currentFilter.period,
          dealer: this.currentFilter.dealer,
          oil: this.arrayOil.filter(item => item).join(',')
        })
        this.resultsData = { ...results }
        // this.showResults = true
      },
      async fetchResults (oil, itsInit = true) {
        if (itsInit) {
          this.showResults = false
        }
        const results = await this.fetchTechMapResultsData({
          workpackages: this.$route.query.workpackages || null,
          period: this.currentFilter.period,
          dealer: this.currentFilter.dealer,
          oil
        })
        this.resultsData = { ...results }
        if (itsInit) {
          this.showResults = true
        }
      },

      async onOilChange (oil, column) {
        if (column !== undefined) {
          const prevValue = this.arrayOil[column] || ''
          this.arrayOil[column] = oil

          if (!this.oilChangeFirstTime && prevValue !== oil) {
            await this.fetchResults(this.arrayOil.filter(item => item != null).join(','), false)
          }
        }

        const newOilQuery = this.arrayOil.filter(item => item != null).join(',')

        if (!this.oilChangeFirstTime && this.oil !== newOilQuery) {
          const newQuery = {
            ...this.$route.query,
            oil: newOilQuery
          }
          if (JSON.stringify(this.$route.query) !== JSON.stringify(newQuery)) {
            this.$router.replace({
              path: this.$route.path,
              query: newQuery
            })
          }
        }

        this.oil = newOilQuery
        this.oilChangeFirstTime = false

        this.$store.commit('SET_OIL_ID', newOilQuery)
      },
      checkNull (data) {
        if (data) return data
        return ''
      },
      modelTextState () {
        this.modelText = get(this, '$store.state.model.modelText', '') || get(this, 'pageData.car.toyotaModelName', '')
      },
      modelIdState () {
        this.modelId = get(this, '$store.state.model.modelId', '') || get(this, 'pageData.car.toyotaModelId', '')
      },
      pushDataToDataLayer (params = {} || window.tngaDataLayer, pushDataLayer = false) {
        window.dataLayer = window.dataLayer || []
        window.tngaDataLayer = { ...params }

        if (pushDataLayer) window.dataLayer.push({ 'event': 'workflowevent' })
      }
    }
  }
</script>

<style lang="scss">
#app.isMd {
  .tech-map-page__sticky-header {
    top: 30px;
  }
}

.tech-map-page {
  &__title {
    margin-bottom: 0;
  }

  &__sticky-header {
    position: sticky;
    top: 0;
    z-index: 9999;
    padding-top: 35px;
    background: white;

    &::before {
      position: absolute;
      bottom: 0;
      left: 0;
      z-index: -1;
      width: 100%;
      height: 100%;
      content: '';
      background: white;
    }

    &::after {
      position: absolute;
      bottom: 0;
      left: 0;
      z-index: -2;
      width: 100%;
      height: 5%;
      content: '';
      box-shadow: 0 1px 15px rgba($c-gray-dark, 0.5);
      opacity: 0;
      transition: opacity 0.3s;

      @include mobile {
        display: none;
      }
    }

    &_stuck {
      &::after {
        opacity: 1;
      }
    }

    @include tablet {
      padding-top: 25px;
    }

    @include mobile {
      position: static !important;
      padding: 20px 15px 0 15px;
    }
  }

  &__range {
    padding-bottom: 55px;

    @include mobile {
      padding-bottom: 20px;
    }
  }

  &__dropdowns {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 35px;

    @include mobile {
      flex-direction: column;
      margin-bottom: 20px;
    }
  }

  &__dropdown {
    width: 335px;

    @include mobile {
      width: 100%;
      margin-bottom: 5px;
    }

    &_disable {
      cursor: default;

      .select__field {
        @include hover {
          background-color: $c-gray-light !important;
        }
      }

      .select__arrow {
        display: none;
      }
    }
  }
}
</style>
